import Router from 'next/router';
import { useState, createContext, ReactNode, useContext, useEffect, useRef } from 'react';

interface TransitioningProviderProps {
  children: ReactNode;
}

interface TransactionsContextData {
  isTransitioning: boolean;
  handleStartTransitioning: () => void;
  handleStopTransitioning: () => void;
}

const TransitioningContext = createContext({} as TransactionsContextData);

export function TransitioningProvider({ children }: TransitioningProviderProps) {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const alreadyContainedMgid = useRef(false);

  const appendScriptMgid = () => {
    const existingComponentMobile = document.getElementById(`mgidContainerCTmobile`);
    const existingComponentDesktop = document.getElementById(`mgidContainerCTdesktop`);
    if (!existingComponentDesktop || !existingComponentMobile) return;

    const existingScriptMobile = document.getElementById(`mgidScriptmobile`);
    const existingScriptDesktop = document.getElementById(`mgidScriptdesktop`);
    if (existingScriptMobile && existingScriptDesktop) return;

    const divDesktop = document.createElement('div');
    divDesktop.id = 'M901213ScriptRootC1525795';
    divDesktop.className = 'mgidDesktop';

    const divMobile = document.createElement('div');
    divMobile.id = 'M901213ScriptRootC1524573';
    divMobile.className = 'mgidMobile';

    const scriptDesktop = document.createElement('script');
    scriptDesktop.id = `mgidScriptmobile`;
    scriptDesktop.src = `https://jsc.mgid.com/c/a/canaltech.com.br.1525795.js`;
    scriptDesktop.type = 'text/javascript';
    scriptDesktop.async = true;
    scriptDesktop.defer = true;

    const scriptMobile = document.createElement('script');
    scriptMobile.id = `mgidScriptmobile`;
    scriptMobile.src = `https://jsc.mgid.com/c/a/canaltech.com.br.1524573.js`;
    scriptMobile.type = 'text/javascript';
    scriptMobile.async = true;
    scriptMobile.defer = true;

    const mgidParentDesktop = document.getElementById(`mgidContainerCTdesktop`);
    mgidParentDesktop.appendChild(divDesktop);
    mgidParentDesktop.appendChild(scriptDesktop);

    const mgidParentMobile = document.getElementById(`mgidContainerCTmobile`);
    mgidParentMobile.appendChild(divMobile);
    mgidParentMobile.appendChild(scriptMobile);
  };

  const decreaseScriptMgid = () => {
    const mgidParentDesktop = document.getElementById('mgidScriptdesktop');
    if (!mgidParentDesktop) return;

    while (mgidParentDesktop && mgidParentDesktop.firstChild) {
      mgidParentDesktop.removeChild(mgidParentDesktop.firstChild);
    }

    const mgidParentMobile = document.getElementById('mgidScriptmobile');
    if (!mgidParentMobile) return;

    while (mgidParentMobile && mgidParentMobile.firstChild) {
      mgidParentMobile.removeChild(mgidParentMobile.firstChild);
    }

    appendScriptMgid();
  };

  const handleStartTransitioning = () => {
    setIsTransitioning(true);

    const existingComponentMgid = document.getElementById('mgidScriptmobile');
    alreadyContainedMgid.current = !!existingComponentMgid;
  };

  // Não dispara ViewContent: quem manda o page-view para a Conversions API é o
  // effect de `_app.tsx`, keyed por `router.asPath` — ele já cobre a carga inicial
  // E as navegações SPA. Disparar aqui também gerava 2 eventos por navegação, com
  // ids distintos (a Meta não deduplica) e contagem dobrada no Events Manager.
  const handleStopTransitioning = (url?: string) => {
    const { dataLayer } = window;

    const sendVirtualNavegation = () => dataLayer.push({ event: 'virtualNavegation' });

    const minDelay = new Promise<void>(resolve => setTimeout(resolve, 2000));
    const idleReady = new Promise<void>(resolve => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => resolve(), { timeout: 5000 });
      } else {
        setTimeout(resolve, 2000);
      }
    });

    Promise.all([minDelay, idleReady]).then(sendVirtualNavegation);

    setIsTransitioning(false);

    if (alreadyContainedMgid.current) decreaseScriptMgid();
  };

  useEffect(() => {
    Router.events.on('routeChangeStart', handleStartTransitioning);
    Router.events.on('routeChangeComplete', handleStopTransitioning);
    return () => {
      Router.events.off('routeChangeStart', handleStartTransitioning);
      Router.events.off('routeChangeComplete', handleStopTransitioning);
    };
  }, []);

  return (
    <TransitioningContext.Provider value={{ isTransitioning, handleStartTransitioning, handleStopTransitioning }}>{children}</TransitioningContext.Provider>
  );
}

export function useTransition() {
  const context = useContext(TransitioningContext);

  return context;
}
